import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Upload, Sparkles, CreditCard, Download, Image as ImageIcon, Mail, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * « Je crée ma caricature » — FENÊTRE INTÉGRÉE (accueil public).
 * INVITÉ (aucun compte) via /api/public/caricature/* + jeton privé opaque. CLIENT connecté via
 * /api/client/caricature/* (INCHANGÉ). Rattachement magasin CÔTÉ SERVEUR (M1). Paiement SumUp hébergé.
 *
 * INVITÉ v7.1 (fix paiement : photo réduite + idempotence + garde redirection) : email OBLIGATOIRE au checkout. Après paiement : génération -> images EN LIGNE -> ENVOI par email
 * (phase « Envoi en cours » bornée par un délai). ACCEPTÉ par le service = « email envoyé » (jamais « reçu »).
 * Échec / refus / délai dépassé : pas de succès -> saisie d'une AUTRE adresse valide et renvoi des images de la
 * MÊME commande payée (aucun nouveau paiement/génération). Conservation privée limitée puis purge côté serveur.
 */
const OFFER_FALLBACK = [
  { id: 'STANDARD_SOLO', price_eur: 1.01, quality: 'medium', image_count: 1, custom: false },
  { id: 'PREMIUM_HD_SOLO', price_eur: 1.21, quality: 'high', image_count: 1, custom: false },
  { id: 'STANDARD_PACK', price_eur: 1.50, quality: 'medium', image_count: 5, custom: false },
  { id: 'PREMIUM_HD_TRIO_CUSTOM', price_eur: 2.40, quality: 'high', image_count: 3, custom: true },
];
const BADGE = { STANDARD_PACK: 'caric.badge.bestPrice', PREMIUM_HD_TRIO_CUSTOM: 'caric.badge.custom' };
const DEFAULT_OFFER = 'PREMIUM_HD_TRIO_CUSTOM';
const GKEY = (sid) => `caric_guest::${sid}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validEmail = (e) => { const v = (e || '').trim(); return v.length <= 200 && EMAIL_RE.test(v); };
const SEND_TIMEOUT_MS = 45000; // délai max côté client (le serveur borne l'envoi à ~25 s)

const CaricatureModal = ({ open, onClose }) => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const isClient = !!(user && user.role === 'client');
  const base = isClient ? '/client/caricature' : '/public/caricature';

  const fileRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [offer, setOffer] = useState(DEFAULT_OFFER);
  const [customText, setCustomText] = useState('');
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle|paying|verifying|generating|sending|done|emailRetry|expired|error
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);
  const [rights, setRights] = useState(false);
  const [email, setEmail] = useState('');            // email de commande (checkout invité)
  const [sess, setSess] = useState(null);            // { sid, g } pour renvoi
  const [emailInfo, setEmailInfo] = useState(null);  // { emailed, masked, timedOut, pending, expired }
  const [resendEmail, setResendEmail] = useState(''); // adresse corrigée pour un renvoi
  const [idemKey, setIdemKey] = useState('');          // clé d'idempotence (reprise du MÊME checkout, pas de doublon)

  const offers = (config?.offers?.length ? config.offers : OFFER_FALLBACK);
  const sel = offers.find((o) => o.id === offer) || OFFER_FALLBACK[3];
  const isCustom = !!sel.custom;
  const customMax = config?.custom_max || 300;

  useEffect(() => {
    if (!open) return;
    api.get(`${base}/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false, offers: OFFER_FALLBACK }));
  }, [open, base, api]);

  const fetchImages = useCallback(async (sid, imgToken, images, g) => {
    const out = [];
    for (const im of (images || [])) {
      try {
        const params = isClient ? { token: imgToken, slot: im.slot } : { g, token: imgToken, slot: im.slot };
        const r = await api.get(`${base}/${sid}/image`, { params });
        out.push({ slot: im.slot, src: `data:image/png;base64,${r.data.image_base64}` });
      } catch (_) { /* ignore */ }
    }
    return out;
  }, [api, base, isClient]);

  const inlineResults = (images) => (images || [])
    .filter((im) => im && im.image_base64)
    .map((im) => ({ slot: im.slot, src: `data:image/png;base64,${im.image_base64}` }));

  // INVITÉ : envoi (ou renvoi) par email. Affiche « Envoi en cours » jusqu'à la réponse (délai max).
  const doSend = useCallback(async (sid, g, emailOverride) => {
    setPhase('sending'); setError(''); setBusy(true);
    try {
      const body = emailOverride ? { email: (emailOverride || '').trim() } : {};
      const r = await api.post(`${base}/${sid}/send-email`, body, { params: { g }, timeout: SEND_TIMEOUT_MS });
      const d = r.data || {};
      if (d.purged) {
        setEmailInfo({ emailed: !!d.emailed, masked: d.email_masked || '', expired: !!d.expired });
        setPhase('expired');
      } else if (d.state === 'accepted' || d.emailed) {
        setEmailInfo({ emailed: true, masked: d.email_masked || '' });
        setPhase('done');
      } else {
        setEmailInfo({ emailed: false, masked: d.email_masked || '', uncertain: d.state === 'uncertain', bounced: d.state === 'bounced', pending: true });
        setResendEmail(''); setPhase('emailRetry');
      }
    } catch (e) {
      // 400 adresse invalide, 409 envoi en cours/cooldown, délai réseau… -> imposer une correction et un renvoi
      setEmailInfo((prev) => ({ ...(prev || {}), emailed: false, pending: true }));
      setError(e.response?.data?.detail || t('caric.retryTimeout'));
      setResendEmail(''); setPhase('emailRetry');
    } finally {
      setBusy(false);
    }
  }, [api, base, t]);

  // Retour SumUp : invité ?sid&g / client ?session_id → vérifier → générer → (invité) envoyer.
  useEffect(() => {
    if (!open) return;
    const usp = new URLSearchParams(window.location.search);
    const sid = usp.get('sid') || usp.get('session_id');
    if (!sid) return;
    let g = usp.get('g') || '';
    if (!isClient && !g) { try { g = localStorage.getItem(GKEY(sid)) || ''; } catch (_) {} }
    let cancelled = false;
    (async () => {
      setPhase('verifying'); setError(''); setBusy(true);
      try {
        const vp = isClient ? {} : { params: { g } };
        const v = await api.get(`${base}/verify/${sid}`, vp);
        if (cancelled) return;
        if (!v.data.is_paid) { setPhase('error'); setError(t('caric.notPaid')); return; }
        setPhase('generating');
        const gp = isClient ? [{}, { timeout: 360000 }] : [null, { params: { g }, timeout: 360000 }];
        const gr = await api.post(`${base}/generate/${sid}`, gp[0], gp[1]);
        if (cancelled) return;
        if (isClient) {
          const imgs = await fetchImages(sid, gr.data.image_token, gr.data.images, g);
          setResults(imgs); setBusy(false); setPhase('done');
          return;
        }
        // INVITÉ v4
        setSess({ sid, g });
        try { localStorage.removeItem(GKEY(sid)); } catch (_) {}
        window.history.replaceState({}, document.title, window.location.pathname);
        if (gr.data.purged) {
          setEmailInfo({ emailed: !!gr.data.emailed, masked: gr.data.email_masked || '', expired: !!gr.data.expired });
          setBusy(false); setPhase('expired');
          return;
        }
        setResults(inlineResults(gr.data.images));
        if (gr.data.emailed) {
          setEmailInfo({ emailed: true, masked: gr.data.email_masked || '' });
          setBusy(false); setPhase('done');
        } else {
          await doSend(sid, g); // envoi initial -> « Envoi en cours »
        }
      } catch (e) {
        if (!cancelled) { setPhase('error'); setError(e.response?.data?.detail || t('caric.genError')); setBusy(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onFile = (file) => {
    if (!file || !file.type.startsWith('image/')) { setError(t('caric.invalidFile')); return; }
    if (file.size > 25 * 1024 * 1024) { setError(t('caric.tooBig')); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = String(e.target.result);
      // FIX prod : on REDIMENSIONNE la photo côté client (max 1600 px, JPEG) AVANT l'upload.
      // Une photo iPhone (~20 Mo) faisait échouer/timeout le checkout ; réduite -> upload léger, rapide, fiable.
      const applySmall = (val) => {
        setPreview(val); setB64(String(val).split(',')[1]); setResults([]); setError('');
        setIdemKey(Math.random().toString(36).slice(2) + Date.now().toString(36)); // nouvelle commande -> nouvelle clé
      };
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const MAX = 1600; let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
            if (Math.max(w, h) > MAX) { const sc = MAX / Math.max(w, h); w = Math.round(w * sc); h = Math.round(h * sc); }
            const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
            cv.getContext('2d').drawImage(img, 0, 0, w, h);
            applySmall(cv.toDataURL('image/jpeg', 0.85));
          } catch (_) { applySmall(dataUrl); }
        };
        img.onerror = () => { setError(t('caric.invalidFile')); };
        img.src = dataUrl;
      } catch (_) { applySmall(dataUrl); }
    };
    reader.readAsDataURL(file);
  };

  const pay = async () => {
    if (!b64) { setError(t('caric.needPhoto')); return; }
    if (!isClient && !validEmail(email)) { setError(t('caric.emailRequired')); return; }
    if (!consent) { setError(t('caric.consentRequired')); return; }
    if (!rights) { setError(t('caric.rightsRequired')); return; }
    setBusy(true); setPhase('paying'); setError('');
    try {
      const uiLang = (() => { try { return localStorage.getItem('app_lang') || 'fr'; } catch (_) { return 'fr'; } })();
      const r = await api.post(`${base}/checkout`, {
        offer, image_base64: b64, consent: true, rights_confirm: true,
        custom_instructions: isCustom ? (customText || '').slice(0, customMax) : '', lang: uiLang,
        idempotency_key: idemKey || undefined,
        ...(isClient ? {} : { email: (email || '').trim() }),
      });
      // FIX prod : ne JAMAIS rediriger sans une vraie URL SumUp absolue.
      // Si la requête a été mise en file hors-ligne (réseau instable -> réponse synthétique __offline) ou si
      // checkout_url est absente/relative, on affiche une erreur au lieu de naviguer vers "undefined"
      // (ce qui tombait sur la route * -> /login -> connexion staff).
      if (r && r.data && r.data.__offline) { setPhase('idle'); setBusy(false); setError(t('caric.payError')); return; }
      const checkoutUrl = r && r.data && r.data.checkout_url;
      if (typeof checkoutUrl !== 'string' || !/^https?:\/\//i.test(checkoutUrl)) {
        setPhase('idle'); setBusy(false); setError(t('caric.payError')); return;
      }
      if (!isClient && r.data.guest_token) { try { localStorage.setItem(GKEY(r.data.session_id), r.data.guest_token); } catch (_) {} }
      window.location.href = checkoutUrl;
    } catch (e) {
      setPhase('idle'); setBusy(false); setError(e.response?.data?.detail || t('caric.payError'));
    }
  };

  const downloadOne = (src, i) => {
    const a = document.createElement('a'); a.href = src; a.download = `caricature-${i + 1}-${Date.now()}.png`; a.click();
  };

  const offerName = (id) => t(`caric.o.${id}.name`);
  const offerDesc = (id) => t(`caric.o.${id}.desc`);
  const qualityLabel = (q) => (q === 'high' ? t('caric.qualityHigh') : t('caric.qualityMedium'));
  const imagesLabel = (n) => (n > 1 ? t('caric.imagesMany', { n }) : t('caric.imageOne'));
  const priceLine = (o) => (o.image_count > 1 ? t('caric.forTotal', { price: formatCurrency(o.price_eur) }) : formatCurrency(o.price_eur));

  const resetAll = () => { setResults([]); setPhase('idle'); setPreview(null); setB64(null); setConsent(false); setRights(false); setEmailInfo(null); setSess(null); setResendEmail(''); setIdemKey(''); };

  if (!open) return null;

  const ImagesGrid = () => (
    results.length > 0 ? (
      <div className={`grid ${results.length > 1 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'} gap-3`}>
        {results.map((im, i) => (
          <figure key={im.slot} className="space-y-2">
            <img src={im.src} alt={`${t('caric.yourCaricature')} ${i + 1}`} className="w-full rounded-xl border border-white/10" />
            <button type="button" onClick={() => downloadOne(im.src, i)} className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-pink-600 text-white text-xs font-semibold hover:bg-pink-500" data-testid={`caric-download-${im.slot}`}><Download className="w-3.5 h-3.5" /> {t('caric.download')}</button>
          </figure>
        ))}
      </div>
    ) : null
  );

  return (
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto py-6 px-3" data-testid="caricature-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-3xl rounded-2xl border border-pink-500/30 bg-[#0f1117] shadow-2xl p-4 sm:p-6 space-y-5" role="dialog" aria-modal="true" aria-label={t('caric.title')}>
        <button type="button" onClick={onClose} className="absolute top-3 end-3 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" data-testid="caric-close" aria-label="Fermer">
          <X className="w-5 h-5" />
        </button>
        <header>
          <h1 className="text-2xl font-black text-white flex items-center gap-2"><Sparkles className="w-6 h-6 text-pink-400" aria-hidden="true" /> {t('caric.title')}</h1>
          <p className="text-sm text-gray-400 mt-1">{t('caric.subtitle', { price: formatCurrency(1.01) })}</p>
        </header>

        {config && !config.enabled && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200 text-sm" data-testid="caric-disabled">{t('caric.disabled')}</div>
        )}

        {/* ENVOI EN COURS */}
        {phase === 'sending' && (
          <section className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center space-y-2" data-testid="caric-sending">
            <Mail className="w-10 h-10 mx-auto text-pink-400 animate-pulse" aria-hidden="true" />
            <p className="text-white font-semibold">{t('caric.sending')}</p>
            <p className="text-xs text-gray-500">{t('caric.sendingHint')}</p>
          </section>
        )}

        {/* SUCCÈS : email envoyé (jamais « reçu ») */}
        {phase === 'done' && (
          <section className="rounded-2xl border border-pink-500/30 bg-black/40 p-4 space-y-3" data-testid="caric-result">
            <h2 className="text-white font-bold">{t('caric.yourCaricature')}</h2>
            <ImagesGrid />
            {!isClient && emailInfo && emailInfo.emailed && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-emerald-200 text-xs space-y-1" data-testid="caric-emailed">
                <p className="flex items-center justify-center gap-2 font-semibold"><Mail className="w-4 h-4" aria-hidden="true" /> {emailInfo.masked ? t('caric.emailSentTo', { email: emailInfo.masked }) : t('caric.emailSentTitle')}</p>
                <p className="text-emerald-200/80 text-center">{t('caric.emailSentCheck')}</p>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => { setResendEmail(''); setError(''); setPhase('emailRetry'); }} className="text-[11px] underline text-emerald-200/90 hover:text-white" data-testid="caric-not-received">{t('caric.notReceived')}</button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              <button type="button" onClick={resetAll} className="px-4 py-2 rounded-lg bg-white/10 text-gray-200 text-sm hover:bg-white/20">{t('caric.another')}</button>
            </div>
          </section>
        )}

        {/* RENVOI IMPOSÉ : envoi non confirmé -> adresse corrigée + renvoi (mêmes images) */}
        {phase === 'emailRetry' && (
          <section className="rounded-2xl border border-amber-500/40 bg-black/40 p-4 space-y-3" data-testid="caric-email-retry">
            <h2 className="text-white font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" aria-hidden="true" /> {t('caric.retryTitle')}</h2>
            <p className="text-amber-200 text-sm">{emailInfo && emailInfo.uncertain ? t('caric.retryUncertain') : (emailInfo && emailInfo.bounced ? t('caric.retryBounced') : t('caric.retryFailed'))}</p>
            <ImagesGrid />
            <div className="rounded-xl border border-white/15 bg-black/30 p-3 space-y-2">
              <label htmlFor="caric-resend-input" className="text-white font-semibold text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-pink-400" aria-hidden="true" /> {t('caric.resendEmailLabel')} <span className="text-pink-400">*</span></label>
              <input id="caric-resend-input" type="email" inputMode="email" autoComplete="email" value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value.slice(0, 200))} placeholder={t('caric.emailPlaceholder')}
                className="w-full rounded-lg bg-black/40 border border-white/15 p-2 text-sm text-gray-100 placeholder-gray-500 focus:border-pink-400 outline-none"
                data-testid="caric-resend-input" />
              {resendEmail && !validEmail(resendEmail) && <p className="text-[11px] text-amber-300">{t('caric.emailInvalid')}</p>}
              <button type="button" onClick={() => sess && doSend(sess.sid, sess.g, resendEmail)}
                disabled={busy || !validEmail(resendEmail)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-pink-600 text-white text-sm font-semibold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="caric-resend-btn">
                <RefreshCw className="w-4 h-4" aria-hidden="true" /> {t('caric.resend')}
              </button>
            </div>
            {error && <p className="text-red-300 text-xs" data-testid="caric-retry-error">{error}</p>}
          </section>
        )}

        {/* EXPIRÉ / PURGÉ */}
        {phase === 'expired' && (
          <section className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center space-y-2" data-testid="caric-expired">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" aria-hidden="true" />
            <p className="text-white font-semibold">{t('caric.expiredTitle')}</p>
            {emailInfo && emailInfo.emailed
              ? <p className="text-xs text-gray-400">{emailInfo.masked ? t('caric.expiredDelivered', { email: emailInfo.masked }) : t('caric.emailSentTitle')}</p>
              : <p className="text-xs text-gray-400">{t('caric.expired')}</p>}
            <div className="pt-1"><button type="button" onClick={resetAll} className="px-4 py-2 rounded-lg bg-white/10 text-gray-200 text-sm hover:bg-white/20">{t('caric.another')}</button></div>
          </section>
        )}

        {(phase === 'verifying' || phase === 'generating') && (
          <section className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center space-y-2" data-testid="caric-progress">
            <ImageIcon className="w-10 h-10 mx-auto text-pink-400 animate-pulse" aria-hidden="true" />
            <p className="text-white font-semibold">{phase === 'verifying' ? t('caric.verifying') : t('caric.generating')}</p>
            <p className="text-xs text-gray-500">{sel.image_count > 1 ? t('caric.generatingHintPack') : t('caric.generatingHint')}</p>
          </section>
        )}

        {error && phase !== 'emailRetry' && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-red-200 text-sm" data-testid="caric-error">{error}</div>}

        {(phase === 'idle' || phase === 'paying') && !(config && !config.enabled) && (
          <>
            <section
              className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${preview ? 'border-pink-500/50' : 'border-white/15 hover:border-pink-500/40'}`}
              onClick={() => fileRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
              onDragOver={(e) => e.preventDefault()}
              role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
              aria-label={t('caric.upload')} data-testid="caric-upload">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="caric-file" />
              {preview ? (<img src={preview} alt="" className="max-h-72 mx-auto rounded-xl" />) : (
                <div className="space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-gray-400" aria-hidden="true" />
                  <p className="text-white font-semibold">{t('caric.upload')}</p>
                  <p className="text-xs text-gray-500">{t('caric.uploadHint')}</p>
                </div>
              )}
            </section>

            {!isClient && (
              <section className="rounded-2xl border border-pink-500/30 bg-black/30 p-4 space-y-2" data-testid="caric-email">
                <label htmlFor="caric-email-input" className="text-white font-semibold text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-pink-400" aria-hidden="true" /> {t('caric.emailLabel')} <span className="text-pink-400">*</span>
                </label>
                <p className="text-xs text-gray-400">{t('caric.emailHint')}</p>
                <input id="caric-email-input" type="email" inputMode="email" autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value.slice(0, 200))} placeholder={t('caric.emailPlaceholder')}
                  className="w-full rounded-lg bg-black/40 border border-white/15 p-2 text-sm text-gray-100 placeholder-gray-500 focus:border-pink-400 outline-none"
                  data-testid="caric-email-input" />
                {email && !validEmail(email) && <p className="text-[11px] text-amber-300">{t('caric.emailInvalid')}</p>}
              </section>
            )}

            <section className="space-y-3" data-testid="caric-offers">
              <h2 className="text-white font-bold">{t('caric.chooseOffer')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {offers.map((o) => {
                  const selected = offer === o.id;
                  return (
                    <button key={o.id} type="button" onClick={() => setOffer(o.id)} aria-pressed={selected}
                      className={`relative text-start rounded-2xl border p-4 ${selected ? 'border-pink-500 ring-2 ring-pink-500/40 bg-pink-500/[.06]' : 'border-white/10 hover:border-white/30 bg-black/30'}`}
                      data-testid={`caric-offer-${o.id}`}>
                      {BADGE[o.id] && (<span className="absolute -top-2 end-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-black">{t(BADGE[o.id])}</span>)}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white font-bold">{offerName(o.id)}</span>
                        <span className="text-pink-300 font-bold whitespace-nowrap">{priceLine(o)}</span>
                      </div>
                      <p className="text-xs text-gray-300 mt-1">{offerDesc(o.id)}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{imagesLabel(o.image_count)} · {qualityLabel(o.quality)}{o.custom ? ` · ${t('caric.customizable')}` : ''}</p>
                    </button>
                  );
                })}
              </div>

              {isCustom && (
                <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/[.06] p-4 space-y-2" data-testid="caric-custom">
                  <label htmlFor="caric-custom-input" className="text-white font-semibold text-sm block">{t('caric.custom.label')}</label>
                  <p className="text-xs text-gray-300">{t('caric.custom.help')}</p>
                  <textarea id="caric-custom-input" value={customText} onChange={(e) => setCustomText(e.target.value.slice(0, customMax))} maxLength={customMax}
                    placeholder={t('caric.custom.placeholder')} rows={3}
                    className="w-full rounded-lg bg-black/40 border border-white/15 p-2 text-sm text-gray-100 placeholder-gray-500 focus:border-fuchsia-400 outline-none" data-testid="caric-custom-input" />
                  <div className="flex items-center justify-between text-[11px] text-gray-500"><span>{t('caric.custom.examples')}</span><span>{(customText || '').length}/{customMax}</span></div>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[.06] p-4 text-sm space-y-2" data-testid="caric-consent">
              <h2 className="text-white font-bold">{t('caric.consentTitle')}</h2>
              <ul className="list-disc pl-5 text-gray-300 text-xs space-y-0.5">
                <li>{t('caric.consentAi')}</li><li>{t('caric.consentPurpose')}</li><li>{t('caric.consentRetention')}</li>
                <li>{t('caric.consentDelete')}</li><li>{t('caric.consentRights')}</li><li>{t('caric.consentMinors')}</li>
              </ul>
              <label className="flex items-start gap-2 text-gray-200 cursor-pointer pt-1">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="caric-consent-check" />
                <span>{t('caric.consentAccept')}</span>
              </label>
              <label className="flex items-start gap-2 text-gray-200 cursor-pointer">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} className="mt-0.5" data-testid="caric-rights-check" />
                <span>{t('caric.rightsConfirm')}</span>
              </label>
            </section>

            <section className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3" data-testid="caric-recap">
              <h2 className="text-white font-bold text-sm">{t('caric.recap.title')}</h2>
              <dl className="text-sm text-gray-300 space-y-1">
                <div className="flex justify-between"><dt>{t('caric.recap.offer')}</dt><dd className="text-white font-semibold" data-testid="caric-recap-offer">{offerName(sel.id)}</dd></div>
                <div className="flex justify-between"><dt>{t('caric.recap.images')}</dt><dd className="text-white">{imagesLabel(sel.image_count)}</dd></div>
                <div className="flex justify-between"><dt>{t('caric.recap.quality')}</dt><dd className="text-white">{qualityLabel(sel.quality)}</dd></div>
                <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><dt className="font-semibold">{t('caric.recap.total')}</dt><dd className="text-pink-300 font-bold text-base" data-testid="caric-recap-total">{formatCurrency(sel.price_eur)}</dd></div>
              </dl>
              <div className="text-center pt-1">
                <button type="button" onClick={pay} disabled={!b64 || !consent || !rights || busy || (config && !config.enabled) || (!isClient && !validEmail(email))}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-pink-600 text-white font-bold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="caric-pay">
                  <CreditCard className="w-4 h-4" aria-hidden="true" /> {busy && phase === 'paying' ? t('caric.redirecting') : t('caric.payTotal', { price: formatCurrency(sel.price_eur) })}
                </button>
              </div>
              {!isClient && <p className="text-xs text-gray-500 text-center">{t('caric.retentionNote', { hours: (config?.retention?.result_days ? config.retention.result_days * 24 : 72) })}</p>}
              <p className="text-xs text-gray-500 text-center">{t('caric.securedBySumup')}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CaricatureModal;
